import { makeStyles, useTheme } from '@material-ui/styles'
import clsx from 'clsx'
import React, { type PropsWithChildren, useEffect, useState } from 'react'
import { LoadingGifs } from 'app/constants'
import Text from 'core/elements/Text'
import type Theme from 'core/themes/model'
import { memoize } from 'utils/misc'
import BlueDarkTiles from './loader-svgs/BlueDarkTiles'
import BlueLightTiles from './loader-svgs/BlueLightTiles'
import BluePinkDarkTiles from './loader-svgs/BluePinkDarkTiles'
import BluePinkLightTiles from './loader-svgs/BluePinkLightTiles'
import Ellipsis from './loader-svgs/Ellipsis'

const defaultLoaderHeight = 80
export interface ProgressProps {
  loading: boolean
  overlay?: boolean
  inline?: boolean
  renderLoadingImage?: boolean
  renderContentOnMount?: boolean
  message?: string
  loadingImage?: LoadingGifs
  inlineClassName?: string
  loadingImageHeight?: number
  className?: string
}

const imageMap = {
  [LoadingGifs.BluePinkTiles]: {
    light: BluePinkLightTiles,
    dark: BluePinkDarkTiles,
  },
  [LoadingGifs.BlueTiles]: {
    light: BlueLightTiles,
    dark: BlueDarkTiles,
  },
  [LoadingGifs.Ellipsis]: Ellipsis,
}

const getLoadingImage = memoize((inline, imageType, themeType) => {
  if (inline) {
    return imageMap[LoadingGifs.Ellipsis]
  }
  return imageMap[imageType][themeType]
})

export default function Progress({ children, ...props }: PropsWithChildren<ProgressProps>) {
  const {
    loading = false,
    overlay = false,
    inline = false,
    renderLoadingImage = true,
    renderContentOnMount = true,
    message = 'Loading',
    loadingImage = LoadingGifs.BlueTiles,
    loadingImageHeight = defaultLoaderHeight,
    inlineClassName,
    className,
  } = props
  const theme: Theme = useTheme()
  const classes = useStyles()
  const [state, setState] = useState({
    loadedOnce: false,
  })

  useEffect(() => {
    if (props.loading) {
      return
    }
    setState({
      loadedOnce: true,
    })
  }, [props.loading])

  const LoadingImageComponent = renderLoadingImage
    ? getLoadingImage(inline, loadingImage, theme.palette.type)
    : React.Fragment

  const shouldRenderStatus = loading
  const shouldNotRenderContent = !children || (!renderContentOnMount && !state.loadedOnce)

  return (
    <div
      className={clsx(classes.root, 'progress-root', {
        [classes.rootInline]: inline,
      })}
    >
      {shouldRenderStatus && (
        <div
          className={clsx(classes.status, 'progress-status', {
            [classes.statusInline]: inline,
            [classes.statusOverlayed]:
              overlay && loading && (renderContentOnMount || state.loadedOnce),
            [classes.statusOverlayedInline]:
              inline && overlay && loading && (renderContentOnMount || state.loadedOnce),
          })}
        >
          {renderLoadingImage && (
            <LoadingImageComponent className={classes.img} height={loadingImageHeight} />
          )}
          {message && (
            <Text
              className={clsx(
                classes.message,
                { [classes.messageInline]: inline },
                inlineClassName,
              )}
              variant={inline ? 'body2' : 'subtitle2'}
            >
              {message}
            </Text>
          )}
        </div>
      )}
      {!shouldNotRenderContent && (
        <div
          className={clsx(classes.content, className, 'progressContent', {
            loading,
            [classes.hiddenContent]: loading && !overlay,
            [classes.hiddenContentInline]: inline && loading && !overlay,
            [classes.contentLoading]: loading,
            [classes.contentLoadingInline]: inline && loading,
          })}
        >
          {children}
        </div>
      )}
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'grid',
    position: 'relative',
    height: '100%',
    minWidth: 350,
  },
  rootInline: {
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    position: 'relative',
    minWidth: 60,
    minHeight: 'unset',
  },
  message: {
    order: 0,
  },
  messageInline: {
    order: -1,
  },
  img: {
    opacity: 1,
  },
  status: {
    display: 'grid',
    justifyContent: 'center',
    justifyItems: 'center',
    alignItems: 'center',
    rowGap: 16,
    gridAutoFlow: 'row',
    gridAutoRows: 'max-content',
    alignContent: 'center',
    padding: '32px 0',
    minHeight: 'max-content',
  },
  statusInline: {
    gridAutoFlow: 'column',
    padding: 'unset',
    minHeight: 'unset',
  },
  statusOverlayed: {
    position: 'absolute',
    zIndex: 100,
    inset: '0 0',
    minWidth: 'unset',
  },
  statusOverlayedInline: {
    inset: 'auto 0 8px 0',
    minWidth: 150,
  },
  content: {
    width: '100%',
    transition: 'opacity .2s ease, filter .2s ease',
  },
  contentLoading: {
    opacity: 0.35,
    filter: 'blur(1px)',
  },
  contentLoadingInline: {
    opacity: 0.6,
    filter: 'unset',
  },
  hiddenContent: {
    visibility: 'hidden',
    display: 'inherit',
  },
  hiddenContentInline: {
    visibility: 'visible',
    display: 'none',
  },
}))
